import { useHistory, useParams } from 'react-router-dom';
import useAuth from '../../hooks/use-auth';
import { ActivitySubmission } from '../../hydra';
import { ACTIVITY_PAGE_TYPE, ActivityLayout } from '../../components/Activity';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import { Helmet } from 'react-helmet-async';
import { ACTIVITY_TYPE, ActivityProvider } from '../../contexts/activity';
import Navbar, { NAVBAR_HEIGHT } from '../../components/Navbar';
import React from 'react';
import { AIWorkflow } from '../../contexts/ai-workflow';
import AIChat from '../../components/AIChat';
import { getProjectPageTitle, ROUTES } from '../../routing';
import {
  createProsumerProjectAIReview,
  getProsumerProject,
  saveProsumerProject,
  validateProsumerProjectAnswerAttempt,
} from '../../api/prosumer';
import { useProsumerProject } from '../../hooks/use-hydra/prosumer';
import { AnswerStreamSubjectProvider } from '../../answer-stream/context';
import { ANSWER_STREAM_SUBJECT_TYPE } from '../../answer-stream/types';

const StyledNavbar = styled(Navbar)`
  position: static;
`;

const StyledAIChat = styled(AIChat)`
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px - 32px);
  bottom: calc(${NAVBAR_HEIGHT}px + 16px);
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);
`;

export default function ProsumerProject() {
  const { getTokenSilently } = useAuth();
  const { projectSlug } =
    useParams<{
      projectSlug: string;
    }>();

  const projectState = useProsumerProject(projectSlug);
  const submissionId = projectState.value?.latest_submission?.id ?? -1;

  const getActivity = async () => {
    const token = await getTokenSilently();
    return getProsumerProject(token, {
      projectSlug,
    });
  };
  const history = useHistory();

  const goToHome = async (): Promise<void> => {
    toast.info('Returning home...', {
      autoClose: 2000,
    });
    setTimeout(() => {
      history.push(ROUTES.HOME.getPath());
    }, 2000);
  };

  const breadCrumbs = [
    {
      label: 'Home',
      url: ROUTES.HOME.getPath(),
    },
    {
      label: 'Projects',
      url: ROUTES.PROSUMER.PROJECTS_DASHBOARD.getPath(),
    },
    {
      label: projectState.value?.title || 'Project',
      url: null,
    },
  ];

  const saveSubmission = async (submission: ActivitySubmission) => {
    const token = await getTokenSilently();

    await saveProsumerProject(token, {
      submission,
      projectSlug,
    });
  };

  const createAIReview = async ({
    activitySlug,
    submissionId,
  }: {
    activitySlug: string;
    submissionId: number;
  }) => {
    const token = await getTokenSilently();
    return createProsumerProjectAIReview(token, {
      projectSlug: activitySlug,
      sessionId: submissionId,
    });
  };

  const { hasAIAccess } = React.useContext(AIWorkflow);

  const project = projectState.value;

  const submitAnswer = React.useCallback(
    async ({
      questionSlug,
      value,
    }: {
      questionSlug: string;
      value: string;
    }) => {
      const token = await getTokenSilently();

      await validateProsumerProjectAnswerAttempt(token, {
        value,
        submissionId,
        questionSlug,
        projectSlug: project?.slug ?? '',
      });
    },
    [getTokenSilently, submissionId, project?.slug]
  );

  return (
    <PageWithAsyncData
      isLoading={projectState.loading}
      error={projectState.error}
      retry={projectState.retry}
      className={''}
    >
      {projectState.value && (
        <>
          <Helmet>
            <title>{getProjectPageTitle(projectState.value.title)}</title>
          </Helmet>
          <AnswerStreamSubjectProvider
            subject={
              submissionId > 0
                ? {
                    subjectType:
                      ANSWER_STREAM_SUBJECT_TYPE.PROSUMER_PROJECT_SESSION,
                    subjectId: submissionId,
                  }
                : null
            }
          >
            <ActivityProvider
              activity={projectState?.value}
              type={ACTIVITY_TYPE.PROJECT}
            >
            <StyledNavbar breadCrumbs={breadCrumbs} />
            {hasAIAccess && project && (
              <div>
                <StyledAIChat
                  contentId={null}
                  missionId={null}
                  activityId={null}
                  projectId={project.exercise.id}
                  discussionContext={'project'}
                  shouldShowChatIcon={false}
                  answerStreamSubjectType={
                    ANSWER_STREAM_SUBJECT_TYPE.PROSUMER_PROJECT_SESSION
                  }
                  answerStreamSubjectId={submissionId}
                />
              </div>
            )}
            <ActivityLayout
              type={ACTIVITY_PAGE_TYPE.PROSUMER}
              goToNextActivity={goToHome}
              saveSubmission={saveSubmission}
              getActivity={getActivity}
              createAIReview={createAIReview}
              dashboardUrl={ROUTES.PROSUMER.PROJECTS_DASHBOARD.getPath()}
              activityState={projectState}
              submitAnswer={submitAnswer}
            />
            </ActivityProvider>
          </AnswerStreamSubjectProvider>
        </>
      )}
    </PageWithAsyncData>
  );
}
